import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useWatch, useFieldArray, Controller, FormProvider, useFormContext } from "react-hook-form";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useMutation, useQuery } from "@apollo/client";
import { useStoreState } from "ezStore";
import { zodResolver } from "@hookform/resolvers/zod";

import { CircularProgress, Grid2 as Grid, Alert, Stack } from "@mui/material";
import { Card, PageHeader, FormGroup, Button, showSnackbar } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";

import DatePicker from "components/cck/DatePicker";
import Helmet from "components/helmet/Helmet";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import NumberInput from "components/cck/NumberInput";

import { Language, DataFormat, hasOptionalFieldPermission, Fields } from "imports/Utils";
import { FormattedDate } from "utils/DateUtils";

import { reeferSchema, ReeferSchemaInput, ReeferSchemaOutput } from "./utils/reefer.schema";
import { createCheckList } from "./utils/ReeferUtils";
import ReeferLocationTypeSelect from "./components/selects/ReeferLocationTypeSelect";

import { GetReeferCheckListDocument } from "__generated__/view/reefer/graphql/reefer.queries.generated";
import { SaveReeferChecklistDocument } from "__generated__/view/reefer/graphql/reefer.mutations.generated";
import { ReeferCheckType } from "__generated__/enums";

const Reefer = () => {
	const { id } = useParams();
	const session = useStoreState(store => store.session);
	const navigate = useNavigate();

	const form = useForm<ReeferSchemaInput, unknown, ReeferSchemaOutput>({
		resolver: zodResolver(reeferSchema),
		defaultValues: {
			location: {
				type: "ship",
				sec: null,
				row: null,
				lay: null,
			},
			plugin: {
				id: null,
				timestamp: null,
				temperature: null,
			},
			plugout: {
				id: null,
				timestamp: null,
				temperature: null,
			},
			setPoint: null,
			checks: [],
		},
	});

	const { control, handleSubmit, reset } = form;

	const [ plugin, plugout ] = useWatch({ control, name: [ "plugin", "plugout" ] });

	const { data, loading: getReeferLoading }  = useQuery(GetReeferCheckListDocument, {
		variables: {
			id: Number(id),
		},
	});

	const [ saveReeferChecklist, { loading } ] = useMutation(SaveReeferChecklistDocument);

	const cargo = data?.cargo;

	const save = (data: ReeferSchemaOutput) => {
		saveReeferChecklist({
			variables: {
				input: {
					id: Number(id),
					reeferChecklistInput: {
						location: data.location ? JSON.stringify(data.location) : undefined,
						plugin: {
							id: data.plugin.id ?? undefined,
							timestamp: data.plugin.timestamp,
							temperature: data.plugin.temperature,
						},
						plugout: {
							id: data.plugout.id ?? undefined,
							timestamp: data.plugout.timestamp ?? undefined,
							temperature: data.plugout.temperature ?? undefined,
						},
						setPoint: data.setPoint ? data.setPoint : undefined,
						checks: data.checks.flatMap(check => check.list).filter(check => check.timestamp != null && check.temperature != null).map(check => ({
							id: check.id ?? 0,
							timestamp: check.timestamp,
							temperature: check.temperature,
						})),
					},
				},
			},
		}).then(({ data }) => {
			const voyageId = data?.saveReeferChecklist?.voyage?.id;
			if (voyageId) {
				showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
				navigate(`/view/voyages/${voyageId}/details?tab=reefer`);
			}
		});
	};

	useEffect(() => {
		if (cargo) {
			const parsedLocation = JSON.parse(cargo.reeferChecklist?.location ?? "{}");

			reset({
				location: {
					type: parsedLocation.type || "ship",
					sec: parsedLocation.sec ?? null,
					row: parsedLocation.row ?? null,
					lay: parsedLocation.lay ?? null,
				},
				plugin: {
					id: cargo.reeferChecklist?.plugin?.id ?? null,
					timestamp: cargo.reeferChecklist?.plugin?.timestamp ?? Date.now(),
					temperature: cargo.reeferChecklist?.plugin?.temperature ?? null,
				},
				plugout: {
					id: cargo.reeferChecklist?.plugout?.id ?? null,
					timestamp: cargo.reeferChecklist?.plugout?.timestamp ?? null,
					temperature: cargo.reeferChecklist?.plugout?.temperature ?? null,
				},
				setPoint: cargo.reeferChecklist?.setPoint ?? null,
				checks: createCheckList({
					checks: cargo?.reeferChecklist?.checks?.map(check => ({
						id: check.id ?? null,
						timestamp: check.timestamp ?? null,
						temperature: check.temperature ?? null,
					})) ?? [],
					pluginTimestamp: cargo.reeferChecklist?.plugin?.timestamp ?? null,
					plugoutTimestamp: cargo.reeferChecklist?.plugout?.timestamp ?? null,
				}),
			});
		}
	}, [ cargo?.id ]);

	if (!cargo || getReeferLoading) {
		return <CircularProgress size={24} />;
	}

	return (
		<FormProvider {...form}>
			<form onSubmit={handleSubmit(save)}>
				<PageHeader>
					<PageHeader.Title>
						{Language("REEFER")}
						<Helmet title={intl.formatMessage({ id: "REEFER" })} />
					</PageHeader.Title>

					<PageHeader.Actions>
						<Button onClick={() => navigate(`/view/voyages/${cargo.voyage?.id}/details?tab=reefer`)} variant="outlined">
							{intl.formatMessage({ id: "CANCEL" })}
						</Button>

						<Button loading={loading} type="submit">
							{intl.formatMessage({ id: "SAVE" })}
						</Button>
					</PageHeader.Actions>
				</PageHeader>

				<Grid container spacing={3}>
					<Grid size={4}>
						<Card>
							{hasOptionalFieldPermission(session, Fields.ORDER, "containerNumber") && (
								<FormGroup label={Language("CONTAINER_NUMBER")} labelPlacement="left-center" overflow="hidden">
									{cargo?.containerNumber || "-"}
								</FormGroup>
							)}

							<FormGroup label={Language("LOADING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
								{cargo?.loadingReference || "-"}
							</FormGroup>

							{hasOptionalFieldPermission(session, Fields.ORDER, "containerType") && (
								<FormGroup label={Language("CARGO_CONTAINER_TYPE")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.containerType(cargo?.containerType)}
								</FormGroup>
							)}

							<FormGroup label={Language("REEFER_OVERVIEW_TEMPERATURE")} labelPlacement="left-center" overflow="hidden">
								{DataFormat.temperature(cargo?.desiredTemperature)}
							</FormGroup>

							<FormGroup label={Language("CARGO_LOADING_PTA")} labelPlacement="left-center" overflow="hidden">
								<FormattedDate date={cargo?.firstLoadingAction?.call?.pta} />
							</FormGroup>

							<FormGroup label={Language("CARGO_LOADING_ATA")} labelPlacement="left-center" overflow="hidden">
								<FormattedDate date={cargo?.firstLoadingAction?.call?.ata} />
							</FormGroup>

							<FormGroup label={Language("REEFER_CHECK_TYPE")} labelPlacement="left-center" overflow="hidden">
								{intl.formatMessage({ id: `REEFER_CHECK_TYPE_${cargo?.reeferCheckType}` })}
							</FormGroup>

							<FormGroup label={Language("REEFER_LOCATION_TYPE")} labelPlacement="left-center" overflow="hidden">
								<Controller
									control={control}
									name="location.type"
									render={({ field }) => <ReeferLocationTypeSelect {...field} clearable={false} /> } />
							</FormGroup>

							<FormGroup label={Language("REEFER_LOCATION_SECTION")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="location.sec"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<NumberInput {...field} decimalScale={0} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							<FormGroup label={Language("REEFER_LOCATION_ROW")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="location.row"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<NumberInput {...field} decimalScale={0} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							<FormGroup label={Language("REEFER_LOCATION_LAYER")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="location.lay"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<NumberInput {...field} decimalScale={0} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</Card>
					</Grid>

					<Grid size={8}>
						<Card>
							<Stack spacing={2}>
								<Grid container spacing={3}>
									<Grid size={3}>
										<FormGroup label={Language("REEFER_PLUGIN_DATE")} overflow="hidden" required>
											<Controller
												name="plugin.timestamp"
												control={control}
												render={({ field, formState }) => (
													<React.Fragment>
														<DatePicker
															{...field}
															onChange={value => field.onChange(value || Date.now())}
															maxDate={plugout.timestamp ? new Date(plugout.timestamp) : undefined}
															showTimeInput />
														<FormErrorMessage name={field.name} errors={formState.errors} />
													</React.Fragment>
												)} />
										</FormGroup>
									</Grid>

									<Grid size={3}>
										<FormGroup label={Language("REEFER_PLUGIN_TEMP")} overflow="hidden" required>
											<Controller
												name="plugin.temperature"
												control={control}
												render={({ field, formState }) => (
													<React.Fragment>
														<NumberInput {...field} decimalScale={1} addonAfter={"°C"} />
														<FormErrorMessage name={field.name} errors={formState.errors} />
													</React.Fragment>
												)} />
										</FormGroup>
									</Grid>

									<Grid size={3}>
										<FormGroup label={Language("REEFER_SETPOINT_TEMP")} overflow="hidden">
											<Controller
												name="setPoint"
												control={control}
												render={({ field, formState }) => (
													<React.Fragment>
														<NumberInput {...field} decimalScale={1} addonAfter={"°C"} />
														<FormErrorMessage name={field.name} errors={formState.errors} />
													</React.Fragment>
												)} />
										</FormGroup>
									</Grid>
								</Grid>

								{(plugin.timestamp && (cargo?.reeferCheckType === ReeferCheckType?.Premium)) ? (
									<div>
										<CheckList />
									</div>
								) : (
									<Grid container spacing={3}>
										<Grid size={12}>
											<Alert severity="warning">
												{intl.formatMessage({ id: "NOT_A_PREMIUM_REEFER" })}
											</Alert>
										</Grid>
									</Grid>
								)}

								<Grid container spacing={3}>
									<Grid size={3}>
										<FormGroup label={Language("REEFER_PLUGOUT_DATE")} overflow="hidden">
											<Controller
												name="plugout.timestamp"
												control={control}
												render={({ field, formState }) => (
													<React.Fragment>
														<DatePicker
															{...field}
															onChange={value => field.onChange(value)}
															minDate={plugin.timestamp ? new Date(plugin.timestamp) : undefined}
															showTimeInput />
														<FormErrorMessage name={field.name} errors={formState.errors} />
													</React.Fragment>
												)} />
										</FormGroup>
									</Grid>

									<Grid size={3}>
										<FormGroup label={Language("REEFER_PLUGOUT_TEMP")} overflow="hidden">
											<Controller
												name="plugout.temperature"
												control={control}
												render={({ field, formState }) => (
													<React.Fragment>
														<NumberInput {...field} decimalScale={1} addonAfter={"°C"} />
														<FormErrorMessage name={field.name} errors={formState.errors} />
													</React.Fragment>
												)} />
										</FormGroup>
									</Grid>
								</Grid>
							</Stack>
						</Card>
					</Grid>
				</Grid>
			</form>
		</FormProvider>
	);
};

const CheckList = () => {

	const { control, getValues } = useFormContext<ReeferSchemaInput, unknown, ReeferSchemaOutput>();

	const { fields, replace } = useFieldArray({
		control,
		name: "checks",
	});

	const [ pluginTimestamp, plugoutTimestamp ] = useWatch({
		control,
		name: [ "plugin.timestamp", "plugout.timestamp" ],
	});

	usePropertyChangeEffect(() => {
		if (pluginTimestamp) {
			const currentChecks = getValues("checks") ?? [];
			const flatChecks = currentChecks
				.flatMap(item => item.list)
				.filter(item => item.timestamp || item.temperature);

			const newChecks = createCheckList({
				checks: flatChecks,
				pluginTimestamp,
				plugoutTimestamp,
			});
			replace(newChecks);
		} else {
			replace([]);
		}
	}, [ pluginTimestamp, plugoutTimestamp ]);

	return (
		<div>
			<Grid container spacing={3}>
				<Grid size={3}>
					<strong>{intl.formatMessage({ id: "REEFER_OVERVIEW_DATE" })}</strong>
				</Grid>

				{Array.from(Array(3).keys()).map((index) => (
					<Grid size={3} key={index}>
						<Grid container spacing={1}>
							<Grid size={6}>
								<strong>{intl.formatMessage({ id: "REEFER_TIME" })}</strong>
							</Grid>

							<Grid size={6}>
								<strong>{intl.formatMessage({ id: "REEFER_TEMP" })}</strong>
							</Grid>
						</Grid>
					</Grid>
				))}
			</Grid>

			{fields.map((item, index) => (
				<CheckListRow key={item.id} index={index} pluginTimestamp={pluginTimestamp} />
			))}
		</div>
	);
};

interface CheckListRowProps {
	index: number;
	pluginTimestamp: number | null;
}

const CheckListRow = ({ index, pluginTimestamp }: CheckListRowProps) => {
	const { control } = useFormContext<ReeferSchemaInput, unknown, ReeferSchemaOutput>();
	const date = moment(pluginTimestamp).add(index, "days").valueOf();

	const rowList = useWatch({
		control,
		name: `checks.${index}.list`,
	});

	return (
		<Grid container spacing={3}>
			<Grid size={3}>
				<span style={{ alignItems: "center", display: "flex", height: "100%", fontWeight: 600, fontSize: 13 }}>
					<FormattedDate date={date} format="ll" />
				</span>
			</Grid>

			{Array.from({ length: 3 }).map((_, columnIndex) => {
				const prevDate = rowList?.[columnIndex - 1]?.timestamp;
				const rounding = 5 * 60 * 1000;
				const remainder = Math.ceil(Date.now() / rounding) * rounding;

				const placeholder = prevDate
					? moment(prevDate).add(8, "hours").format("HH:mm")
					: moment(remainder).add(columnIndex * 8, "hours").format("HH:mm");

				return (
					<Grid size={3} key={columnIndex}>
						<Grid container spacing={1}>
							<Grid size={6}>
								<Controller
									name={`checks.${index}.list.${columnIndex}.timestamp`}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker
												{...field}
												onChange={value => {
													if (value) {
														const hours = moment(value).hours();
														const minutes = moment(value).minutes();
														field.onChange(moment(date).hour(hours).minute(minutes).valueOf());
													} else {
														field.onChange(null);
													}
												}}
												showTimeSelect
												showTimeSelectOnly
												placeholderText={placeholder}
											/>
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)}
								/>
							</Grid>

							<Grid size={6}>
								<Controller
									name={`checks.${index}.list.${columnIndex}.temperature`}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<NumberInput {...field} decimalScale={1} addonAfter={"°C"} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)}
								/>
							</Grid>
						</Grid>
					</Grid>
				);
			})}
		</Grid>
	);
};

export default Reefer;
